import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import TextField from "@material-ui/core/TextField";
import KeyIcon from "@material-ui/icons/VpnKeyOutlined";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";

// SEGREDOS DA CONTA (STU-186): credenciais que os Scripts leem com
// studio.segredo("NOME"). O valor fica cifrado no servidor e nunca volta —
// nem nesta tela, nem no log. Trocar o valor substitui; apagar é definitivo.

// paleta local — mesmos valores das telas de lista (Coletores/Automações)
const T = {
  bg: "#F6F7F9", panel: "#FFFFFF", line: "#E6E9ED", lineSoft: "#EEF0F3",
  ink: "#1B222A", ink2: "#5B6470", ink3: "#929AA4",
  mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
};

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: T.bg },
  wrap: { width: "100%", maxWidth: 1100, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2.5) },
  headIcon: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF0F3", color: "#5B6470", marginTop: 2 },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: T.ink },
  subtitle: { color: T.ink3, fontSize: 13, marginTop: 4, maxWidth: 760, lineHeight: 1.5 },
  grow: { flexGrow: 1 },
  code: { fontFamily: T.mono, fontSize: 12, background: "#F1F3F5", borderRadius: 6, padding: "1px 6px" },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  row: { display: "flex", alignItems: "center", gap: 14, background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, padding: "12px 12px 12px 16px" },
  avatar: { width: 40, height: 40, borderRadius: 999, flexShrink: 0, background: "#EFF8D6", color: "#5f7a12", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 20 } },
  main: { flex: 1, minWidth: 0 },
  infoLabel: { fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink3, fontWeight: 600, marginBottom: 4 },
  name: { fontFamily: T.mono, fontSize: 13.5, color: T.ink, fontWeight: 600 },
  col: { width: 170, flexShrink: 0 },
  mono: { fontFamily: T.mono, fontSize: 12, color: T.ink3 },
  empty: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, padding: theme.spacing(7, 3), textAlign: "center", color: T.ink3, fontSize: 13.5, lineHeight: 1.6 },
  btnLight: { textTransform: "none", fontSize: 13, fontWeight: 600, color: T.ink, background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, padding: "6px 14px" },
  dlg: { padding: theme.spacing(2.5), display: "flex", flexDirection: "column", gap: 14, width: 440, maxWidth: "calc(100vw - 32px)" },
  dlgTitle: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 17, color: T.ink },
  dlgActions: { display: "flex", justifyContent: "flex-end", gap: 8 },
}));

const when = d => (d ? format(new Date(d), "dd/MM/yyyy HH:mm") : "—");

const Vault = () => {
  const classes = useStyles();
  const m = useMenuStyles();
  const { confirm } = useConfirm();
  const [secrets, setSecrets] = useState(null);
  const [dlg, setDlg] = useState(null); // { name, value, editing }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/secrets");
      setSecrets(data.secrets || []);
    } catch (err) {
      toastError(err);
      setSecrets([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`/secrets/${encodeURIComponent(dlg.name.trim())}`, { value: dlg.value });
      toast.success("Segredo salvo.");
      setDlg(null);
      load();
    } catch (err) {
      toastError(err);
    }
  };
  const remove = async name => {
    if (!(await confirm({ title: "Apagar segredo", message: `Apagar "${name}"? Os scripts que usam studio.segredo("${name}") passam a falhar.`, confirmText: "Apagar", danger: true }))) return;
    try {
      await api.delete(`/secrets/${encodeURIComponent(name)}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <span className={classes.headIcon}><KeyIcon /></span>
          <div className={classes.grow}>
            <div className={classes.title}>Segredos</div>
            <div className={classes.subtitle}>
              Senhas, tokens e chaves de API que os scripts usam com <span className={classes.code}>await studio.segredo(&quot;NOME&quot;)</span>.
              O valor fica cifrado e nunca aparece de novo — nem aqui, nem no log.
            </div>
          </div>
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={() => setDlg({ name: "", value: "", editing: false })}>
            Novo segredo
          </Button>
        </div>

        {secrets && secrets.length === 0 ? (
          <div className={classes.empty}>
            Nenhum segredo ainda.<br />
            Cadastre a credencial de uma API e use <b>studio.segredo(&quot;NOME&quot;)</b> num script para ler.
          </div>
        ) : (
          <div className={classes.list}>
            {(secrets || []).map(s => (
              <div key={s.name} className={classes.row}>
                <span className={classes.avatar}><KeyIcon /></span>
                <div className={classes.main}>
                  <div className={classes.infoLabel}>Nome</div>
                  <div className={classes.name}>{s.name}</div>
                </div>
                <div className={classes.col}>
                  <div className={classes.infoLabel}>Atualizado em</div>
                  <span className={classes.mono}>{when(s.updatedAt)}</span>
                </div>
                <Button className={classes.btnLight} onClick={() => setDlg({ name: s.name, value: "", editing: true })}>Trocar valor</Button>
                <Tooltip title="Apagar"><IconButton size="small" onClick={() => remove(s.name)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!dlg} onClose={() => setDlg(null)} PaperProps={{ className: m.dialogPaper }}>
        {dlg && (
          <div className={classes.dlg}>
            <div className={classes.dlgTitle}>{dlg.editing ? `Trocar o valor de ${dlg.name}` : "Novo segredo"}</div>
            {!dlg.editing && (
              <TextField variant="outlined" size="small" label="Nome" placeholder="ALGAR_CLIENT_SECRET" autoFocus value={dlg.name}
                onChange={e => setDlg({ ...dlg, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })} helperText="Letras, números e _" />
            )}
            <TextField variant="outlined" size="small" label="Valor" type="password" autoFocus={dlg.editing} value={dlg.value}
              onChange={e => setDlg({ ...dlg, value: e.target.value })} autoComplete="new-password" helperText="Depois de salvo, o valor não é mostrado de novo." />
            <div className={classes.dlgActions}>
              <Button className={classes.btnLight} onClick={() => setDlg(null)}>Cancelar</Button>
              <Button variant="contained" color="primary" disableElevation disabled={!dlg.name.trim() || !dlg.value} onClick={save}>Salvar</Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Vault;
